import { useVirtualizer } from '@tanstack/react-virtual';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Clock, FileText, FolderUp, Info, Trash2, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import Spinner from '@/components/shared/Spinner';
import UploadActivityCallout from '@/components/shared/UploadActivityCallout';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useUploadProgress } from '@/hooks/useUploadProgress';
import { backendClient } from '@/lib/api-client';
import { formatFolderPath } from '@/lib/folderPathUtils';
import { getUploadSourceLabel } from '@/lib/sourceUtils';
import { type BatchProgress, batchFailedWithUnfinishedFiles, fetchUploadBatchesPage, rememberPendingKnowledgeCleanupCount } from '@/lib/uploadProgressManager';
import { removeTimestampPrefix } from '@/utils/file-utils';

const DUPLICATE_FAILURE_CATEGORY = 'duplicate';

const OLDER_BATCHES_PAGE_SIZE = 10;
const LIVE_POLL_LIMIT = 10;
const LOAD_MORE_THRESHOLD_PX = 80;
const BATCH_FILES_PAGE_SIZE = 100;

interface BatchFile {
  guid: string;
  document_guid: string | null;
  file_reference_guid: string | null;
  file_name: string;
  display_name: string | null;
  folder_path: string | null;
  folder_name: string | null;
  processing_status: 'uploading' | 'uploaded' | 'pending' | 'processing' | 'completed' | 'failed';
  error_message?: string | null;
  failure_category?: string | null;
  created_at: string;
}

interface BatchFilesResponse {
  files: BatchFile[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

interface UploadBatchesSummaryResponse {
  pending_knowledge_cleanup_count?: number;
}

interface BatchFilesPaginationState {
  total: number;
  nextOffset: number;
  hasMore: boolean;
}

const getApiErrorDetail = (err: unknown): string | undefined => {
  return (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
};

// Virtualized batch file list for performance with large batches
const VirtualizedBatchFileList = React.memo(({
  files,
  hasMore,
  isLoadingMore,
  handleFileClick,
  onLoadMore,
}: {
  files: BatchFile[];
  hasMore: boolean;
  isLoadingMore: boolean;
  handleFileClick: (documentGuid: string | null) => void;
  onLoadMore: () => void;
}) => {
  const parentRef = React.useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: files.length + (hasMore ? 1 : 0),
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      if (index >= files.length) return 36;
      const file = files[index];
      const isCompleted = file.processing_status === 'completed';
      const isFailed = file.processing_status === 'failed';
      const hasSubtext = (isCompleted && (file.folder_name || file.folder_path)) || (isFailed && file.error_message);
      return hasSubtext ? 48 : 32;
    },
    overscan: 10,
  });

  const fileMeasureKey = files
    .map(file => `${file.guid}:${file.processing_status}:${file.failure_category || ''}:${file.folder_name || file.folder_path || ''}:${file.error_message || ''}`)
    .join('|');

  // Force remeasure when files change (e.g., status updates add folder names)
  React.useEffect(() => {
    void fileMeasureKey;
    virtualizer.measure();
  }, [fileMeasureKey, virtualizer]);

  const handleFileListScroll = React.useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    const el = parentRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <div
      ref={parentRef}
      onScroll={handleFileListScroll}
      className="max-h-[200px] overflow-y-auto"
      style={{ maxWidth: '365px' }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          if (virtualRow.index >= files.length) {
            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className="flex items-center justify-center py-2"
              >
                {isLoadingMore ? <Spinner size="xs" /> : null}
              </div>
            );
          }

          const file = files[virtualRow.index];
          const isCompleted = file.processing_status === 'completed';
          const isFailed = file.processing_status === 'failed';
          const isSkippedDuplicate = isFailed && file.failure_category === DUPLICATE_FAILURE_CATEGORY;
          const isUploading = file.processing_status === 'uploading';
          const isProcessing = file.processing_status === 'uploaded' || file.processing_status === 'processing' || file.processing_status === 'pending';

          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div
                onClick={() => {
                  if (isCompleted && file.file_reference_guid) {
                    handleFileClick(file.file_reference_guid);
                  }
                }}
                className={`text-xs p-1.5 rounded-sm flex items-start gap-2 bg-slate-50 ${
                  isCompleted && file.file_reference_guid
                    ? 'hover:bg-accent cursor-pointer'
                    : 'cursor-default'
                }`}
                style={{ maxWidth: '365px' }}
              >
                <div className="shrink-0 mt-0.5">
                  {isUploading || isProcessing ? (
                    <Spinner size="xs" />
                  ) : isSkippedDuplicate ? (
                    <Info className="h-3 w-3 text-muted-foreground" />
                  ) : isFailed ? (
                    <XCircle className="h-3 w-3 text-amber-500" />
                  ) : (
                    <FileText className="h-3 w-3 text-green-500" />
                  )}
                </div>
                <div className="min-w-0 overflow-hidden" style={{ maxWidth: '337px' }}>
                  <p
                    className={`truncate ${isCompleted && file.file_reference_guid ? 'text-blue-600 hover:underline' : ''}`}
                    title={file.display_name || removeTimestampPrefix(file.file_name)}
                  >
                    {file.display_name || removeTimestampPrefix(file.file_name)}
                  </p>
                  {isCompleted && (file.folder_name || file.folder_path) && (
                    <p className="text-[10px] text-muted-foreground truncate">
                      {file.folder_name || formatFolderPath(file.folder_path)}
                    </p>
                  )}
                  {isSkippedDuplicate && file.error_message && (
                    <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                      {file.error_message}
                    </p>
                  )}
                  {!isSkippedDuplicate && isFailed && file.error_message && (
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-[10px] text-amber-600 truncate mt-0.5">
                            {file.error_message}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="max-w-[300px] text-xs">
                          <p>{file.error_message}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

VirtualizedBatchFileList.displayName = 'VirtualizedBatchFileList';

export default function UploadProgressCenter() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { openDocumentPanel } = useSidePanel();
  const { selectedCase: currentMatterGuid } = useCaseContext();
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());

  const [batchFiles, setBatchFiles] = useState<Record<string, BatchFile[]>>({});
  const [batchFilesPagination, setBatchFilesPagination] = useState<Record<string, BatchFilesPaginationState>>({});
  const [loadingBatches, setLoadingBatches] = useState<Set<string>>(new Set());
  const [loadingMoreBatchFiles, setLoadingMoreBatchFiles] = useState<Set<string>>(new Set());
  const prevBatchesRef = useRef<Map<string, { completedFiles: number; status: string }>>(new Map());
  const loadingBatchFilePagesRef = useRef<Set<string>>(new Set());
  const batchFilesRef = useRef<Record<string, BatchFile[]>>({});

  // Older batches loaded via infinite scroll (below the live-polled window)
  const [olderBatches, setOlderBatches] = useState<BatchProgress[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingMoreRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const [pendingKnowledgeCleanupCount, setPendingKnowledgeCleanupCount] = useState<number | null>(null);

  // Get batches filtered by current matter GUID to prevent leakage across matter tabs
  const {
    activeBatches: allBatches,
    pendingKnowledgeCleanupCount: cachedPendingKnowledgeCleanupCount,
  } = useUploadProgress(currentMatterGuid);

  // Reset expanded batches and pagination state when popover closes
  useEffect(() => {
    if (!open) {
      setExpandedBatches(new Set());
      setBatchFiles({});
      setBatchFilesPagination({});
      setLoadingBatches(new Set());
      setLoadingMoreBatchFiles(new Set());
      prevBatchesRef.current.clear();
      loadingBatchFilePagesRef.current.clear();
      setOlderBatches([]);
      setHasMore(true);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [open]);

  // Reset paginated older batches if the user switches matters while the popover is open
  // biome-ignore lint/correctness/useExhaustiveDependencies: This state reset intentionally runs when the selected matter changes.
  useEffect(() => {
    setOlderBatches([]);
    setExpandedBatches(new Set());
    setBatchFiles({});
    setBatchFilesPagination({});
    setHasMore(true);
    setLoadingMore(false);
    setLoadingBatches(new Set());
    setLoadingMoreBatchFiles(new Set());
    loadingMoreRef.current = false;
    loadingBatchFilePagesRef.current.clear();
  }, [currentMatterGuid]);

  // Merge live-polled batches with paginated older batches; live values win on conflict
  const liveBatchIds = useMemo(
    () => new Set(allBatches.map(b => b.batchTaskId)),
    [allBatches]
  );
  const mergedBatches = useMemo(
    () => [...allBatches, ...olderBatches.filter(b => !liveBatchIds.has(b.batchTaskId))],
    [allBatches, olderBatches, liveBatchIds]
  );

  // Check if any uploads are active for the header badge
  const hasActiveUploads = allBatches.some(b => b.status === 'processing' || b.status === 'uploading');
  const visiblePendingKnowledgeCleanupCount =
    pendingKnowledgeCleanupCount ?? cachedPendingKnowledgeCleanupCount;
  const hasKnowledgeCleanup = visiblePendingKnowledgeCleanupCount > 0;

  useEffect(() => {
    setPendingKnowledgeCleanupCount(cachedPendingKnowledgeCleanupCount);
  }, [cachedPendingKnowledgeCleanupCount]);

  useEffect(() => {
    batchFilesRef.current = batchFiles;
  }, [batchFiles]);

  const loadMoreOlderBatches = useCallback(async () => {
    if (!currentMatterGuid || loadingMoreRef.current || !hasMore) return;
    // If the live poll itself didn't fill its window, there can't be older batches
    if (allBatches.length < LIVE_POLL_LIMIT) {
      setHasMore(false);
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const offset = mergedBatches.length;
      const next = await fetchUploadBatchesPage(currentMatterGuid, OLDER_BATCHES_PAGE_SIZE, offset);
      if (next.length < OLDER_BATCHES_PAGE_SIZE) setHasMore(false);
      if (next.length > 0) {
        // Dedupe only against current olderBatches; collisions with the live-polled
        // window are filtered downstream by mergedBatches, so we don't need to
        // capture liveBatchIds here (which would be stale by the time the updater runs).
        setOlderBatches(prev => {
          const seen = new Set<string>(prev.map(b => b.batchTaskId));
          const additions = next.filter(b => !seen.has(b.batchTaskId));
          return additions.length > 0 ? [...prev, ...additions] : prev;
        });
      }
    } catch (err) {
      // Stop further auto-loads after a failure; otherwise every scroll near the
      // bottom would re-trigger and spam toasts. Closing/reopening the popover
      // resets hasMore=true so the user can retry.
      toast.error(getApiErrorDetail(err) || 'Failed to load more uploads');
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [currentMatterGuid, hasMore, allBatches.length, mergedBatches.length]);

  // rAF-throttle the scroll handler — reading scrollHeight/scrollTop/clientHeight
  // forces synchronous layout, and scroll fires faster than a frame on trackpads.
  // Coalesce to one layout read per frame to keep paint smooth.
  const handleScroll = useCallback(() => {
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const el = scrollContainerRef.current;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
        loadMoreOlderBatches();
      }
    });
  }, [loadMoreOlderBatches]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open || !currentMatterGuid) {
      return;
    }

    let cancelled = false;

    const fetchPendingCleanupCount = async () => {
      try {
        const response = await backendClient.get<UploadBatchesSummaryResponse>(
          `/correspondence/${currentMatterGuid}/upload-batches`,
          { params: { limit: 1 } }
        );
        if (!cancelled) {
          const count = response.data.pending_knowledge_cleanup_count || 0;
          rememberPendingKnowledgeCleanupCount(currentMatterGuid, count);
          setPendingKnowledgeCleanupCount(count);
        }
      } catch (error) {
        console.error('Failed to fetch pending knowledge cleanup count:', error);
      }
    };

    fetchPendingCleanupCount();
    const interval = window.setInterval(fetchPendingCleanupCount, 8000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [open, currentMatterGuid]);

  // Silently refetch files for expanded batches when batch statuses update
  useEffect(() => {
    let isMounted = true;

    // For each expanded batch, check if it actually changed before refetching.
    // Fetch pages sequentially so a large expanded batch cannot fan out many
    // simultaneous requests during the live polling refresh.
    const fetchBatchData = async () => {
      for (const batchId of Array.from(expandedBatches)) {
        try {
          const batch = allBatches.find(b => b.batchTaskId === batchId);
          if (!isMounted) return;
          if (!batch) continue;

          const prevBatch = prevBatchesRef.current.get(batchId);
          const hasChanged = !prevBatch ||
                            prevBatch.completedFiles !== batch.completedFiles ||
                            prevBatch.status !== batch.status;

          if (hasChanged && isMounted) {
            const loadedCount = batchFilesRef.current[batchId]?.length || BATCH_FILES_PAGE_SIZE;
            const pageCount = Math.max(1, Math.ceil(loadedCount / BATCH_FILES_PAGE_SIZE));
            const responses: BatchFilesResponse[] = [];

            for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
              if (!isMounted) return;
              const response = await backendClient.get<BatchFilesResponse>(`/correspondence/upload-batches/${batchId}/files`, {
                params: { limit: BATCH_FILES_PAGE_SIZE, offset: pageIndex * BATCH_FILES_PAGE_SIZE }
              });
              responses.push(response.data);
            }

            if (!isMounted) return;

            const newFiles = responses.flatMap(response => response.files);
            const lastPage = responses[responses.length - 1];

            // Only update state if the data actually changed
            setBatchFiles(prev => {
              const existingFiles = prev[batchId];

              if (existingFiles && existingFiles.length === newFiles.length) {
                const hasChanges = newFiles.some((newFile, index) => {
                  const existingFile = existingFiles[index];
                  return !existingFile ||
                         existingFile.processing_status !== newFile.processing_status ||
                         existingFile.document_guid !== newFile.document_guid ||
                         existingFile.file_reference_guid !== newFile.file_reference_guid;
                });

                if (!hasChanges) return prev;
              }

              return { ...prev, [batchId]: newFiles };
            });
            setBatchFilesPagination(prev => ({
              ...prev,
              [batchId]: {
                total: responses[0]?.total ?? newFiles.length,
                nextOffset: newFiles.length,
                hasMore: lastPage?.has_more ?? false,
              },
            }));

            if (isMounted) {
              prevBatchesRef.current.set(batchId, {
                completedFiles: batch.completedFiles,
                status: batch.status
              });
            }
          }
        } catch (error) {
          // Silently ignore all errors when component is unmounted
          if (!isMounted) return;
          console.error('Failed to silently fetch batch files:', error);
        }
      }
    };

    fetchBatchData().catch(() => {
      // Final safety net for the outer promise
    });

    return () => {
      isMounted = false;
      // Don't abort - just let requests complete and ignore results via isMounted flag
    };
  }, [allBatches, expandedBatches]); // Include expandedBatches in deps

  const getStatusIcon = (status: BatchProgress['status'], small: boolean = false) => {
    const sizeClass = small ? 'h-3 w-3' : 'h-4 w-4';
    switch (status) {
      case 'uploading':
      case 'processing':
        return <Spinner size={small ? 'xs' : 'sm'} className="border-blue-500 border-r-transparent" />;
      case 'completed':
        return <CheckCircle className={`${sizeClass} text-green-500`} />;
      case 'failed':
        return <AlertTriangle className={`${sizeClass} text-amber-500`} />;
      default:
        return <FolderUp className={`${sizeClass} text-gray-500`} />;
    }
  };

  const getUploadSource = (batch: BatchProgress) => {
    // Use the source field from the database
    if (batch.isBundle) {
      return ui_labels.bundle_singular;
    }
    return getUploadSourceLabel(batch.source);
  };

  const formatTime = (timestamp: number) => {
    try {
      const distance = formatDistanceToNow(new Date(timestamp), { addSuffix: true });
      // Remove "about" for cleaner display (e.g., "about 3 hours ago" -> "3 hours ago")
      return distance.replace(/^about\s+/, '');
    } catch {
      return 'recently';
    }
  };

  const fetchBatchFiles = async (
    batchId: string,
    options: { force?: boolean; offset?: number; append?: boolean } = {}
  ) => {
    const { force = false, offset = 0, append = false } = options;
    const requestKey = `${batchId}:${offset}`;

    if (loadingBatchFilePagesRef.current.has(requestKey)) {
      return;
    }

    if (!force && !append && batchFiles[batchId]) {
      return;
    }

    const isInitialLoad = offset === 0 && !append;
    loadingBatchFilePagesRef.current.add(requestKey);

    if (isInitialLoad) {
      setLoadingBatches(prev => new Set(prev).add(batchId));
    } else {
      setLoadingMoreBatchFiles(prev => new Set(prev).add(batchId));
    }

    try {
      const response = await backendClient.get<BatchFilesResponse>(`/correspondence/upload-batches/${batchId}/files`, {
        params: { limit: BATCH_FILES_PAGE_SIZE, offset }
      });

      // Only update state if the data actually changed to avoid unnecessary re-renders
      setBatchFiles(prev => {
        const existingFiles = prev[batchId];
        const newFiles = response.data.files;

        if (append) {
          const existing = existingFiles || [];
          const existingGuids = new Set(existing.map(file => file.guid));
          const additions = newFiles.filter(file => !existingGuids.has(file.guid));
          return additions.length > 0
            ? { ...prev, [batchId]: [...existing, ...additions] }
            : prev;
        }

        // If we have existing files, check if anything changed
        if (existingFiles && existingFiles.length === newFiles.length) {
          // Check if all file statuses are the same
          const hasChanges = newFiles.some((newFile, index) => {
            const existingFile = existingFiles[index];
            return !existingFile ||
                   existingFile.processing_status !== newFile.processing_status ||
                   existingFile.document_guid !== newFile.document_guid ||
                   existingFile.file_reference_guid !== newFile.file_reference_guid;
          });

          // If nothing changed, return the previous state to avoid re-render
          if (!hasChanges) {
            return prev;
          }
        }

        return {
          ...prev,
          [batchId]: newFiles
        };
      });
      setBatchFilesPagination(prev => ({
        ...prev,
        [batchId]: {
          total: response.data.total,
          nextOffset: offset + response.data.files.length,
          hasMore: response.data.has_more,
        },
      }));
    } catch (error) {
      console.error('Failed to fetch batch files:', error);
    } finally {
      loadingBatchFilePagesRef.current.delete(requestKey);
      if (isInitialLoad) {
        setLoadingBatches(prev => {
          const newSet = new Set(prev);
          newSet.delete(batchId);
          return newSet;
        });
      } else {
        setLoadingMoreBatchFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(batchId);
          return newSet;
        });
      }
    }
  };

  const loadMoreBatchFiles = async (batchId: string) => {
    const pagination = batchFilesPagination[batchId];
    if (!pagination?.hasMore) return;

    await fetchBatchFiles(batchId, {
      offset: pagination.nextOffset,
      append: true,
    });
  };

  // Toggle batch expansion
  const toggleBatchExpansion = async (batchId: string) => {
    const isExpanded = expandedBatches.has(batchId);

    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });

    // Fetch files if expanding and not already loaded
    if (!isExpanded) {
      await fetchBatchFiles(batchId);
    }
  };

  // Handle file click to open document viewer
  const handleFileClick = (documentGuid: string | null) => {
    if (documentGuid) {
      openDocumentPanel({ documentGuid });
      // Keep popover open so user can continue browsing uploads
    }
  };

  const renderKnowledgeCleanupEntry = () => {
    const noun = visiblePendingKnowledgeCleanupCount === 1 ? 'document' : 'documents';

    return (
      <div
        className="p-2 rounded-md bg-background border border-border"
        style={{ width: '380px', maxWidth: '380px' }}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <div className="h-3.5 w-3.5 shrink-0 flex items-center justify-center">
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <span className="text-sm font-medium truncate">
            Finalising deletions
          </span>
          <div className="ml-auto flex items-center gap-1.5 text-xs text-blue-700 shrink-0">
            <Spinner size="xs" className="border-blue-500 border-r-transparent" />
            <span className="animate-pulse">Updating...</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground pl-5">
          <div className="flex items-center gap-2">
            <span>Case knowledge</span>
            <span>•</span>
            <span>{visiblePendingKnowledgeCleanupCount} {noun}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderBatch = (batch: BatchProgress, isActive: boolean = false) => {
    const progress = batch.uploadProgress;
    const isOutlookSync = batch.source === 'outlook';
    const isExpanded = expandedBatches.has(batch.batchTaskId);
    const files = batchFiles[batch.batchTaskId] || [];
    const isLoadingFiles = loadingBatches.has(batch.batchTaskId);
    const filePagination = batchFilesPagination[batch.batchTaskId];
    const isLoadingMoreFiles = loadingMoreBatchFiles.has(batch.batchTaskId);
    const isSyncedDeleteOnlyBatch = batch.syncedDeleteFiles > 0 && batch.syncedDeleteFiles === batch.totalFiles;
    const duplicateFiles = batch.duplicateCount;
    const warningFiles = Math.max(0, batch.failedFiles - duplicateFiles);
    const duplicateNoun = isOutlookSync
      ? (duplicateFiles === 1 ? 'email' : 'emails')
      : (duplicateFiles === 1 ? 'file' : 'files');

    // For metadata row: only show count during processing/completed, not during upload
    let filesText: string | null = null;
    if (batch.status !== 'uploading') {
      if (isOutlookSync) {
        const emailCount = batch.totalFiles;  // Each file is an email for Outlook
        const completedEmails = batch.completedFiles;  // Use actual count
        filesText = `${completedEmails}/${emailCount} ${emailCount === 1 ? 'email' : 'emails'}`;
      } else if (isSyncedDeleteOnlyBatch) {
        filesText = `${batch.syncedDeleteFiles} sync ${batch.syncedDeleteFiles === 1 ? 'delete' : 'deletes'}`;
      } else {
        // During processing/completed: show completed/total
        filesText = `${batch.completedFiles}/${batch.totalFiles} ${batch.totalFiles === 1 ? 'file' : 'files'}`;
      }
    }

    const source = getUploadSource(batch);

    return (
      <Collapsible
        key={batch.batchTaskId}
        open={isExpanded}
        onOpenChange={() => toggleBatchExpansion(batch.batchTaskId)}
        style={{ width: '380px', maxWidth: '380px' }}
      >
        <div className="p-2 rounded-md bg-background border border-border" style={{ width: '380px', maxWidth: '380px' }}>
          {/* Compact header with all info in one line */}
          <div className="flex items-center gap-2 mb-1.5">
            <CollapsibleTrigger asChild>
              <button type="button" className="flex items-center gap-1.5 min-w-0 flex-1">
                <div className="shrink-0">
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </div>
                <span className="text-sm font-medium truncate">
                  {batch.processingDescription}
                </span>
              </button>
            </CollapsibleTrigger>

            {/* Status badge - compact with animated text */}
            {isActive ? (
              <div className="flex items-center gap-1.5 text-xs text-blue-500 shrink-0">
                {getStatusIcon(batch.status, true)}
                <span className="animate-pulse">
                  {batch.status === 'uploading'
                    ? `Uploading ${batch.uploadedFiles}/${batch.totalFiles}...`
                    : 'Processing...'}
                </span>
              </div>
            ) : warningFiles > 0 && warningFiles !== batch.totalFiles ? (
              <div className="flex items-center gap-1 text-xs text-amber-600 shrink-0">
                <AlertTriangle className="h-3 w-3" />
                <span>{warningFiles}</span>
              </div>
            ) : warningFiles > 0 || batchFailedWithUnfinishedFiles(batch) ? (
              <div className="flex items-center gap-1 text-xs text-amber-600 shrink-0">
                <AlertTriangle className="h-3 w-3" />
              </div>
            ) : (
              // All succeeded - show green checkmark
              <div className="flex items-center gap-1 text-xs text-green-600 shrink-0">
                <CheckCircle className="h-3 w-3" />
              </div>
            )}
          </div>

          {/* Metadata row: source, file count (only during processing), time */}
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5 pl-5">
            <div className="flex items-center gap-2">
              <span>{source}</span>
              {filesText && (
                <>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <span>{filesText}</span>
                    {duplicateFiles > 0 && (
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex" tabIndex={0}>
                              <Info className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" align="start" className="max-w-[260px] text-xs">
                            <p>
                              {duplicateFiles} duplicate {duplicateNoun} skipped, already saved in this folder
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatTime(batch.lastUpdate)}
            </div>
          </div>

          <div
            className="mb-1.5 pl-5"
            style={{
              visibility:
                isActive && (batch.status === 'uploading' || batch.status === 'processing')
                  ? 'visible'
                  : 'hidden',
            }}
            aria-hidden={!isActive}
          >
            <Progress value={progress} className="h-1" />
          </div>

          <CollapsibleContent>
            <div className="mt-2 pt-2 border-t border-border">
              {isLoadingFiles && files.length === 0 ? (
                <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
                  <Spinner size="xs" className="mr-2" />
                  {isOutlookSync ? 'Loading emails...' : 'Loading files...'}
                </div>
              ) : files.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-2">
                  No files to display
                </div>
              ) : (
                <VirtualizedBatchFileList
                  files={files}
                  hasMore={filePagination?.hasMore ?? false}
                  isLoadingMore={isLoadingMoreFiles}
                  handleFileClick={handleFileClick}
                  onLoadMore={() => loadMoreBatchFiles(batch.batchTaskId)}
                />
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setPendingKnowledgeCleanupCount(cachedPendingKnowledgeCleanupCount);
    }
    setOpen(newOpen);
  };

  return (
    <div className="flex items-center shrink-0">
      <UploadActivityCallout anchorRef={triggerRef} suppressed={open} />
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="ghost"
            size="icon"
            className="relative transition-all duration-300 text-slate-600 hover:text-slate-900"
          >
            <FolderUp className="h-5 w-5" />
            {hasActiveUploads && (
              <span className="absolute bottom-0 right-0 pointer-events-none">
                <Spinner size="xs" className="border-blue-500 border-r-transparent" />
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end" style={{ maxWidth: '400px', width: '400px' }}>
          <div className="p-3 pl-5 border-b border-border flex items-center justify-between gap-3">
            <h3 className="font-medium">Uploads</h3>
            {currentMatterGuid ? (
              <Button asChild variant="ghost" size="sm" className="h-7 px-2 pr-0 text-xs text-muted-foreground hover:text-foreground">
                <Link
                  to="/matter/$slug/uploads"
                  params={{ slug: currentMatterGuid }}
                  onClick={() => setOpen(false)}
                >
                  View all uploads
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            ) : (
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 pr-0 text-xs" disabled>
                View all uploads
              </Button>
            )}
          </div>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="overflow-y-auto overflow-x-hidden"
            style={{ height: '500px', width: '400px' }}
          >
            <div className="pt-3 px-2 pb-0" style={{ width: '396px' }}>
              {mergedBatches.length === 0 && !hasKnowledgeCleanup ? (
                <div className="p-4 text-center text-muted-foreground">
                  <FolderUp className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No recent imports</p>
                </div>
              ) : (
                <div className="space-y-2 pb-2">
                  {hasKnowledgeCleanup && renderKnowledgeCleanupEntry()}
                  {mergedBatches.map((batch) => {
                    const isActive = batch.status === 'processing' || batch.status === 'uploading';
                    return renderBatch(batch, isActive);
                  })}
                  {loadingMore && (
                    <div className="flex justify-center py-2">
                      <Spinner size="xs" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
